import { useState, useEffect, useContext } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Stack,
  Grid,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
} from "@mui/material";
import moment from "moment";
// Styles
import "../../../Style/pagestyle.scss";
//Components
import EmptyData from "../../Include/EmptyData/EmptyData";
import ViewImage from "../../Student/ViewImage";
import LoadingPage from "../../../Utils/LoadingPage";
import HomeworkAction from "./HomeworkAction";
import CheckHomework from "./CheckHomework";
import RemarkHomework from "./RemarkHomework";
import ExerciseHomework from "./ExerciseHomework";
import {
  SelectStudentByClass,
  SelectSubjectByClassId,
} from "../../Include/DynamicSelect/DynamicSelect";
import { SelectDatePicker } from "../../Include/SelectDatePicker/SelectDatePicker";
import { ButtonCreate } from "../../../Utils/Component";
// Hooks & Context
import useGetStudentHomeworkForCheckList from "../../../Hooks/useGetStudentHomeworkForCheckList";
import { AuthContext } from "../../../Context/AuthContext";

import CreateHomework from "./CreateHomework";

export default function Homework({ classId }) {
  // Context & Localization
  const { t, language } = useContext(AuthContext);

  // GET ITEM FROM LOCAL STORAGE
  const getDate = () => {
    const data = window.localStorage.getItem("date");
    if (
      data === null ||
      data === "null" ||
      data === "" ||
      data === "Invalid Date"
    )
      return null;
    return data;
  };

  const getSubjectId = () => {
    const data = JSON.parse(window.localStorage.getItem("subjectId"));
    return data ?? { id: "", title: "" };
  };

  const getStudentId = () => {
    const data = JSON.parse(window.localStorage.getItem("studentId"));
    return data ?? { id: "", title: "" };
  };

  const getTypeDate = () => {
    const data = window.localStorage.getItem("typeDate");
    return data ?? "All";
  };

  // Local state
  const [date, setDate] = useState(getDate());
  const [typeDate, setTypeDate] = useState(getTypeDate());
  const [subjectId, setSubjectId] = useState(getSubjectId());
  const [studentId, setStudentId] = useState(getStudentId());

  // Fetching homework data
  const { loading, refetch, tableData } = useGetStudentHomeworkForCheckList({
    date: typeDate === "All" ? null : date && moment(date).format("YYYY-MM-DD"),
    classId: classId ?? null,
    subjectId: subjectId?.id ?? null,
    studentId: studentId?.id ?? null,
  });

  // Track window width for responsive table
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const updateDimensions = () => setWidth(window.innerWidth - 50);
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    window.localStorage.setItem("date", date);
  }, [date]);

  useEffect(() => {
    window.localStorage.setItem("subjectId", JSON.stringify(subjectId));
  }, [subjectId]);

  useEffect(() => {
    window.localStorage.setItem("studentId", JSON.stringify(studentId));
  }, [studentId]);

  useEffect(() => {
    window.localStorage.setItem("typeDate", typeDate);
  }, [typeDate]);

  return (
    <div>
      {/* Filters & Header Section */}
      <Box sx={{ marginTop: "10px" }}>
        <Grid container spacing={3}>
          {/* Student Selector */}
          <Grid item xs={6} sm={4} md={3} lg={2.8} xl={2.4}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_student")}
            </Typography>
            <SelectStudentByClass
              value={studentId}
              classId={classId}
              onChange={setStudentId}
              className="text-field"
            />
          </Grid>

          {/* Subject Selector */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_subject")}
            </Typography>
            <SelectSubjectByClassId
              value={subjectId}
              classId={classId}
              onChange={setSubjectId}
              className="text-field"
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_select")}
            </Typography>
            <FormControl fullWidth className="text-field" size="small">
              <Select
                value={typeDate}
                onChange={(e) => setTypeDate(e?.target.value)}
              >
                <MenuItem value={"All"}>{t("thead_all")}</MenuItem>
                <MenuItem value={"SelectDate"}>
                  {t("thead_select_date")}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Picker */}
          <Grid item xs={6} sm={4} md={3} lg={2.4} xl={1.5}>
            {typeDate === "SelectDate" && (
              <Stack
                direction="column"
                alignItems="flex-end"
                justifyContent="flex-end"
                sx={{ height: "100%" }}
              >
                <SelectDatePicker value={date} onChange={setDate} />
              </Stack>
            )}
          </Grid>

          {/* Create Button */}
          <Grid item xs={12} sm={8} md={12} lg={2} xl={4.1}>
            <ButtonCreate
              handleOpen={handleOpen}
              classBtn="btn-create"
              classIcon="icon-add"
              title={t("create")}
            />

            {open && (
              <CreateHomework
                dialogTitle={"Create"}
                open={open}
                classId={classId}
                tableData={tableData}
                setRefetch={refetch}
                handleClose={handleClose}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Homework Table Section */}
      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table">
            {/* Table Head */}
            <TableHead className="header-row">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="2%"
                >
                  {t("thead_no")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  width="8%"
                  align="center"
                >
                  {t("thead_date")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_student")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  width="8%"
                  align="center"
                >
                  {t("thead_gender")}
                </TableCell>

                <TableCell className={t("class-name-header-title")} width="12%">
                  {t("thead_subject")}
                </TableCell>

                <TableCell
                  align="center"
                  className={t("class-name-header-title")}
                  width="17%"
                >
                  {t("thead_exercise")}
                </TableCell>

                <TableCell
                  align="center"
                  className={t("class-name-header-title")}
                  width="18%"
                >
                  {t("thead_status")}
                </TableCell>

                <TableCell
                  className={t("class-name-header-title")}
                  width="15%"
                  align="center"
                >
                  {t("thead_reason")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title-end")}
                  width="5%"
                ></TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body */}
            <TableBody className="body">
              {loading ? (
                <TableRow className="body-row">
                  <TableCell colSpan={10} className="body-cell">
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              ) : tableData?.length > 0 ? (
                tableData?.map((row, index) => (
                  <TableRow key={index} className="body-row">
                    <TableCell className="body-cell-start">
                      {index + 1}
                    </TableCell>

                    <TableCell className="body-cell" align="center">
                      {moment(row?.checkDate).format("DD MMM YYYY")}
                    </TableCell>

                    <TableCell className="body-cell">
                      <ViewImage
                        stuName={`${row?.studentId?.familyName} ${row?.studentId?.studentName}`}
                        profileImg={row?.studentId?.profile}
                      />
                    </TableCell>

                    <TableCell className="body-cell" align="center">
                      {row?.studentId?.gender === "Male"
                        ? t("thead_male")
                        : t("thead_female")}
                    </TableCell>
                    <TableCell className="body-cell">
                      {language === "en"
                        ? row?.subjectId?.enName
                        : row?.subjectId?.khName}
                    </TableCell>

                    <TableCell className="body-cell" align="center">
                      <ExerciseHomework setRefetch={refetch} editData={row} />
                    </TableCell>

                    <TableCell className="body-cell" align="center">
                      <CheckHomework setRefetch={refetch} editData={row} />
                    </TableCell>

                    <TableCell className="body-cell">
                      <RemarkHomework setRefetch={refetch} editData={row} />
                    </TableCell>

                    <TableCell className="body-cell-end" align="right">
                      <HomeworkAction setRefetch={refetch} editData={row} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="body-row">
                  <TableCell colSpan={10} className="body-cell">
                    <EmptyData />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </div>
  );
}
